// Sample data for the Video Call QA review screen, so the layout can be walked through before
// the QA service serves real reviews. Shape mirrors GET /v1/review/{class} in artium-videocall-qa.

export type Severity = "high" | "medium" | "low";

export interface QaFlag {
  id: string;
  source: "incident" | "scorer" | "presence";
  parameter_key: string;
  pillar?: "behaviour" | "academic" | "technical" | null;
  title: string;
  detail: string | null;
  severity: Severity;
  side: string | null;
  t_start: number | null; // seconds from recording start
  t_end: number | null;
  confidence: number | null;
}

export interface QaScoreLine {
  parameter_key: string;
  pillar?: "behaviour" | "academic" | "technical" | null;
  title: string;
  score: number | null;
  scale: string;
  normalized: number | null; // 0–100
  confidence: number | null;
  summary: string | null;
  missing_elements: string[];
  checks?: QaCheck[]; // checklist parameters: one verdict per item, with the frame that shows it
}

export interface QaCheck {
  key: string;
  label: string;
  answer: string | null; // yes | no | cannot_tell, in the question's terms
  met: boolean | null; // passes the standard; null = could not be judged
  note: string | null;
  t_s: number | null; // seconds from recording start
  frame_url: string | null;
  severity?: "high" | "medium" | "low" | null; // worst incident in this card (technical)
}

export interface QaDirectionQuality {
  audio_loss_pct: number | null;
  video_loss_pct: number | null;
  max_loss_pct: number | null;
  audio_frozen_s: number;
  video_frozen_s: number;
  sent_fps: number | null;
  received_fps: number | null;
  frames_received_pct: number | null;
  resolution: string | null;
  measured_s: number;
}

export interface QaRolePresence {
  present_s: number;
  sessions: number;
  rejoins: number;
  first_join_t: number | null;
  last_leave_t: number | null;
  audio_freeze_rate: number | null;
  video_freeze_rate: number | null;
  platforms: string[];
}

export interface QaReview {
  class_id: string;
  status?: string;
  class_schedule_id: string;
  class_type: string;
  instrument: string | null;
  learner_age_band: string | null;
  scheduled_start: string;
  duration_min: number;
  scheduled_duration_min?: number | null;
  teacher: { name: string | null; external_id: string | null };
  learner: { name: string | null; external_id: string | null };
  videos: Record<string, string>;
  qa_score: number | null;
  pillar_scores?: Partial<Record<"behaviour" | "academic" | "technical", number | null>>;
  scores: QaScoreLine[];
  flags: QaFlag[];
  summary: string | null;
  stats: { duration_s?: number; teacher_speech_s?: number; learner_speech_s?: number; silence_s?: number };
  call_quality?: Record<string, QaDirectionQuality>; // "teacher_to_learner" | "learner_to_teacher"
  quality_source?: string | null;
  presence: {
    no_show: "teacher" | "learner" | null;
    no_show_threshold_s: number;
    roles: Record<string, QaRolePresence>;
  } | null;
  transcript: { t_start: number; t_end: number; speaker: string; activity: string; text: string | null }[];
}

const flag = (
  id: string,
  severity: Severity,
  t_start: number,
  t_end: number,
  title: string,
  detail: string,
  side: string,
  source: QaFlag["source"] = "incident",
): QaFlag => ({
  id,
  source,
  parameter_key: source === "incident" ? "technical_incidents" : id,
  title,
  detail,
  severity,
  side,
  t_start,
  t_end,
  confidence: 0.8,
});

export const SAMPLE_REVIEW: QaReview = {
  class_id: "sample",
  class_schedule_id: "C12678834",
  class_type: "paid",
  instrument: "Carnatic Vocal",
  learner_age_band: "10",
  scheduled_start: "2026-10-12T11:30:00Z",
  duration_min: 45,
  teacher: { name: "Sowmya K", external_id: "T8421" },
  learner: { name: "Ananya R", external_id: "L5521" },
  videos: {},
  qa_score: 92,
  scores: [
    {
      parameter_key: "corrective_pedagogy",
      title: "Corrective pedagogy",
      score: 4,
      scale: "1-5",
      normalized: 75,
      confidence: 0.82,
      summary: "Teacher demonstrated, let the student attempt, and corrected pitch on the pa twice.",
      missing_elements: [],
    },
    {
      parameter_key: "closure",
      title: "Closure",
      score: 5,
      scale: "1-5",
      normalized: 100,
      confidence: 0.9,
      summary: "Clear recap and homework set.",
      missing_elements: [],
    },
    {
      parameter_key: "time_management",
      title: "Time management",
      score: 3,
      scale: "1-5",
      normalized: 50,
      confidence: 0.7,
      summary: "Warm-up ran long; theory was squeezed.",
      missing_elements: ["theory"],
    },
    {
      parameter_key: "professional_setup",
      title: "Professional setup",
      score: 4,
      scale: "1-5",
      normalized: 75,
      confidence: 0.76,
      summary: "Good lighting and tanpura audible; teacher briefly out of frame.",
      missing_elements: [],
    },
  ],
  flags: [
    flag("a", "high", 192, 238, "No audio from student", "Student audio volume very low / silent for 46 sec.", "learner"),
    flag("b", "medium", 468, 510, "Teacher not visible", "Teacher out of frame for 42 sec.", "teacher"),
    flag("c", "medium", 1105, 1155, "Student distracted", "Student looking away / not engaged.", "learner", "scorer"),
    flag("d", "low", 1450, 1485, "Possible off-topic conversation", "Non-class related discussion detected.", "teacher", "scorer"),
    flag("e", "high", 1926, 1994, "Connection issue", "Video frozen / poor network for 1 min 8 sec.", "learner"),
    flag("g", "medium", 2354, 2402, "Teacher multitasking", "Teacher looking at phone / other screen.", "teacher", "scorer"),
  ],
  summary:
    "Class was conducted as per plan with good engagement. A few audio and visibility issues detected. " +
    "Teacher followed the planned lesson and provided constructive feedback.",
  stats: { duration_s: 2700, teacher_speech_s: 1320, learner_speech_s: 1080, silence_s: 300 },
  presence: {
    no_show: null,
    no_show_threshold_s: 300,
    roles: {
      teacher: {
        present_s: 2690, sessions: 1, rejoins: 0, first_join_t: 0, last_leave_t: 2690,
        audio_freeze_rate: 0.01, video_freeze_rate: 0.02, platforms: ["Web"],
      },
      learner: {
        present_s: 2580, sessions: 2, rejoins: 1, first_join_t: 40, last_leave_t: 2700,
        audio_freeze_rate: 0.03, video_freeze_rate: 0.08, platforms: ["Android"],
      },
      recorder: {
        present_s: 2700, sessions: 1, rejoins: 0, first_join_t: 0, last_leave_t: 2700,
        audio_freeze_rate: null, video_freeze_rate: null, platforms: ["Linux"],
      },
    },
  },
  transcript: [
    { t_start: 5, t_end: 40, speaker: "teacher", activity: "speech", text: "Namaste Ananya, shall we start with the sarali varisai?" },
    { t_start: 45, t_end: 60, speaker: "learner", activity: "speech", text: "Yes ma'am, I practised it every day." },
    { t_start: 90, t_end: 300, speaker: "teacher", activity: "demo", text: null },
    { t_start: 310, t_end: 500, speaker: "learner", activity: "attempt", text: null },
    { t_start: 520, t_end: 600, speaker: "teacher", activity: "speech", text: "What is the difference between aarohanam and avarohanam?" },
    { t_start: 610, t_end: 640, speaker: "learner", activity: "speech", text: "Aarohanam goes up and avarohanam comes down." },
    { t_start: 835, t_end: 870, speaker: "teacher", activity: "speech", text: "You went flat on the pa. Take a breath before the phrase." },
    { t_start: 2100, t_end: 2180, speaker: "teacher", activity: "speech", text: "Great job today. Practise this varnam for Thursday." },
  ],
};
